// UserCard Component - Compact user preview card

import React from 'react';
import { User, Star, Heart, MapPin } from 'lucide-react';
import { PublicUserProfile } from '../../types';

interface UserCardProps {
  user: PublicUserProfile;
  onClick: (userId: string) => void;
}

export function UserCard({ user, onClick }: UserCardProps) {
  return (
    <button
      onClick={() => onClick(user.id)}
      className="w-full bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow text-left"
    >
      <div className="flex items-center gap-4">
        {/* Avatar */}
        <div className="flex-shrink-0">
          {user.photoURL ? (
            <img
              src={user.photoURL}
              alt={user.displayName}
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
              <User className="w-6 h-6 text-gray-400" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 truncate">
            {user.displayName}
          </h3>

          {user.bio && (
            <p className="text-sm text-gray-500 truncate mt-0.5">
              {user.bio}
            </p>
          )}

          {/* Interests */}
          {user.interests && user.interests.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {user.interests.slice(0, 3).map((interest, idx) => (
                <span
                  key={idx}
                  className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full"
                >
                  {interest}
                </span>
              ))}
              {user.interests.length > 3 && (
                <span className="text-xs text-gray-400">
                  +{user.interests.length - 3}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="flex-shrink-0 flex flex-col items-end gap-1 text-xs text-gray-500">
          {user.privacy.showReviews && user.reviewCount !== undefined && user.reviewCount > 0 && (
            <div className="flex items-center gap-1">
              <Star className="w-3 h-3" />
              <span>{user.reviewCount}</span>
            </div>
          )}
          {user.privacy.showFavorites && user.favoriteCount !== undefined && user.favoriteCount > 0 && (
            <div className="flex items-center gap-1">
              <Heart className="w-3 h-3" />
              <span>{user.favoriteCount}</span>
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

export default UserCard;
